import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { t } from 'i18next';
import {
  Accordion,
  Body,
  Container,
  Description,
  DetailsContainer,
  ExpandButton,
  ExpandCTA,
  Header,
  SelectedCounter,
  Title,
} from './css';
import Checkbox from '../form_elements/Checkbox';
import TextInput from '../form_elements/TextInput';
import { currencyOnly } from '../../../utils/mask';
import { isInRange, isRequired } from '../../../utils/validator';
import { filterServicesToShow, getServicesFromForm, serviceShouldBeDisabled } from './methods';

const oneTo4999 = isInRange(1, 4999.99);

const ServiceGroup = (props) => {
  const {
    group, services, formData, specialCases, isUSShipment, isResidential, fieldsShouldBeBlocked,
  } = props;

  const [itemExpanded, setItemExpanded] = useState(null);

  // Get selected services from formData
  const formSelected = getServicesFromForm(formData);
  const isPrivateResidencePickupSelected = formData && formData.accPRESPU && formData.accPRESPU.selected;
  const checkingServices = ['CPASC', 'INBOND', 'EXSVAL', 'INSDPU', 'INSDDL', 'APPTPU'];
  const servicesToShow = filterServicesToShow(checkingServices, services, isUSShipment, isResidential);
  const apptpuService = services.find((item) => item.code.value === 'APPTPU');
  if (isPrivateResidencePickupSelected && apptpuService) {
    servicesToShow.push(apptpuService);
  }

  const selectedServices = servicesToShow.filter((item) => (formSelected.indexOf(item.code.value) > -1));

  // Auto-expand Insurance for declared value when pre-selected
  const insuranceCodes = ['EXSVAL', 'INSDPU', 'INSDDL'];
  React.useEffect(() => {
    const insuranceSelectedService = servicesToShow.find((service) => {
      const isSelected = selectedServices.some((s) => s.idx === service.idx);
      const isInsurance = insuranceCodes.includes(service.code.value);
      return isSelected && isInsurance;
    });

    if (insuranceSelectedService) {
      setItemExpanded((current) => (current === null ? insuranceSelectedService.id : current));
    }
  }, [selectedServices.length, servicesToShow.length]);

  const toggleExpandItem = (e, itemKey) => {
    e.preventDefault();
    setItemExpanded(itemExpanded === itemKey ? null : itemKey);
  };

  const autodExpand = (id, checker, mandatory) => {
    const isSelected = !checker;

    if (!mandatory) {
      return;
    }

    if (isSelected) {
      setItemExpanded(id);
    }

    if (!isSelected && itemExpanded === id) {
      setItemExpanded(null);
    }
  };

  return (
    <Container>
      <Header>
        <Title>{group.title}</Title>
        <SelectedCounter>
          {selectedServices && selectedServices.length}
          {' '}
          {t('SelectedLabel')}
        </SelectedCounter>
      </Header>
      <Body>

        {servicesToShow.map((service) => {
          const serviceId = service.code.value.toString();
          const isDisabled = serviceShouldBeDisabled(service, specialCases, fieldsShouldBeBlocked);

          const isSelected = (selectedServices.filter((item) => (item.idx === service.idx)).length > 0);
          const isMandatory = (service.mandatoryAmount.value === '1');
          const hasMandatoryFieldSelected = (isSelected && isMandatory);

          return (
            (service.hideInThirdStep.value !== '1')
                        && (
                        <Accordion key={service.id} isExpanded={itemExpanded === service.id}>
                          <Field
                            name={`accessorials.acc${serviceId}.selected`}
                            component={Checkbox}
                            onChange={() => autodExpand(service.id, isSelected, isMandatory)}
                            label={service.title.value}
                            disabled={isDisabled}
                          />
                          <ExpandCTA>
                            {
                                    (service.description || hasMandatoryFieldSelected)
                                    && (
                                    <ExpandButton onClick={(e) => toggleExpandItem(e, service.id)} isExpanded={itemExpanded === service.id}>
                                      <svg viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                          d="M9 1.5L5 5.5L1 1.5"
                                          stroke="#63666A"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </ExpandButton>
                                    )
                                }
                          </ExpandCTA>
                          {
                                (hasMandatoryFieldSelected || service.description)
                                && (
                                <DetailsContainer isExpanded={itemExpanded === service.id}>
                                  {service.description && <Description>{service.description.value}</Description>}
                                  {hasMandatoryFieldSelected
                                        && (
                                        <>
                                          <Field
                                            name={`accessorials.acc${serviceId}.value`}
                                            component={TextInput}
                                            label={t('Amount')}
                                            normalize={currencyOnly}
                                            validate={[isRequired, oneTo4999]}
                                            required
                                          />
                                          <Description>{t('DeclaredValueNote')}</Description>
                                        </>
                                        )}
                                </DetailsContainer>
                                )
                            }
                        </Accordion>
                        )
          );
        })}
      </Body>
    </Container>
  );
};

ServiceGroup.propTypes = {
  group: PropTypes.object,
  services: PropTypes.array,
  formData: PropTypes.object,
  specialCases: PropTypes.object,
  isUSShipment: PropTypes.bool,
  isResidential: PropTypes.bool,
  fieldsShouldBeBlocked: PropTypes.bool,
};

export default ServiceGroup;
